import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { SITE_URL } from './config/journalDefaults';
import { HeaderProvider } from './contexts/HeaderContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { Helmet } from 'react-helmet-async';

const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Articles = lazy(() => import('./pages/Articles'));
const ForAuthors = lazy(() => import('./pages/ForAuthors'));
const ForReviewers = lazy(() => import('./pages/ForReviewers'));
const EditorialBoard = lazy(() => import('./pages/EditorialBoard'));
const Advice = lazy(() => import('./pages/Advice'));
const ManuscriptSubmissionForm = lazy(() => import('./pages/ManuscriptSubmissionForm'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const Policies = lazy(() => import('./pages/Policies'));
const PeerReviewPolicy = lazy(() => import('./pages/PeerReviewPolicy'));
const OpenAccessStatement = lazy(() => import('./pages/OpenAccessStatement'));
const JournalArchives = lazy(() => import('./pages/JournalArchives'));

const EditorLogin = lazy(() => import('./pages/editor/EditorLogin'));
const EditorDashboard = lazy(() => import('./pages/editor/EditorDashboard'));
const EditorProtectedRoute = lazy(() => import('./components/editor/EditorProtectedRoute'));
import { EditorProvider } from './contexts/EditorContext'
const EditorSubmissionDetail = lazy(() => import('./pages/editor/EditorSubmissionDetail'));

const ManagingEditorLogin = lazy(() => import('./pages/managingEditor/ManagingEditorLogin'));
const ManagingEditorDashboard = lazy(() => import('./pages/managingEditor/ManagingEditorDashboard'));
const ManagingEditorProtectedRoute = lazy(() => import('./components/managingEditor/ManagingEditorProtectedRoute'));
import { ManagingEditorProvider } from './contexts/ManagingEditorContext';

const AssociateEditorLogin = lazy(() => import('./pages/associateEditor/AssociateEditorLogin'));
const AssociateEditorDashboard = lazy(() => import('./pages/associateEditor/AssociateEditorDashboard'));
const AssociateEditorProtectedRoute = lazy(() => import('./components/associateEditor/AssociateEditorProtectedRoute'));
import { AssociateEditorProvider } from './contexts/AssociateEditorContext';

const EditorInChiefLogin = lazy(() => import('./pages/editorInChief/EditorInChiefLogin'));
const EditorInChiefDashboard = lazy(() => import('./pages/editorInChief/EditorInChiefDashboard'));
const EditorInChiefProtectedRoute = lazy(() => import('./components/editorInChief/EditorInChiefProtectedRoute'));
import { EditorInChiefProvider } from './contexts/EditorInChiefContext';

const EthicsQualityEditorLogin = lazy(() => import('./pages/ethicsQualityEditor/EthicsQualityEditorLogin'));
const EthicsQualityEditorDashboard = lazy(() => import('./pages/ethicsQualityEditor/EthicsQualityEditorDashboard'));
const EthicsQualityEditorProtectedRoute = lazy(() => import('./components/ethicsQualityEditor/EthicsQualityEditorProtectedRoute'));
import { EthicsQualityEditorProvider } from './contexts/EthicsQualityEditorContext';

const EditorialOfficeLogin = lazy(() => import('./pages/editorialOffice/EditorialOfficeLogin'));
const EditorialOfficeDashboard = lazy(() => import('./pages/editorialOffice/EditorialOfficeDashboard'));
const EditorialOfficeProtectedRoute = lazy(() => import('./components/editorialOffice/EditorialOfficeProtectedRoute'));
import { EditorialOfficeProvider } from './contexts/EditorialOfficeContext';

const EditorialAssistantLogin = lazy(() => import('./pages/editorialAssistant/EditorialAssistantLogin'));
const EditorialAssistantDashboard = lazy(() => import('./pages/editorialAssistant/EditorialAssistantDashboard'));
const EditorialAssistantProtectedRoute = lazy(() => import('./components/editorialAssistant/EditorialAssistantProtectedRoute'));
import { EditorialAssistantProvider } from './contexts/EditorialAssistantContext';

const SectionEditorLogin = lazy(() => import('./pages/sectionEditor/SectionEditorLogin'));
const SectionEditorDashboard = lazy(() => import('./pages/sectionEditor/SectionEditorDashboard'));
const SectionEditorProtectedRoute = lazy(() => import('./components/sectionEditor/SectionEditorProtectedRoute'));
import { SectionEditorProvider } from './contexts/SectionEditorContext';

// Unified login: one front door for Admin + all 9 editorial roles (Author stays separate)
const UnifiedLogin = lazy(() => import('./pages/unified/UnifiedLogin'));
const UnifiedDashboard = lazy(() => import('./pages/unified/UnifiedDashboard'));
import { UnifiedAuthProvider } from './contexts/UnifiedAuthContext';

// Import Author Dashboard Components
const AuthorLogin = lazy(() => import('./pages/author/AuthorLogin'));
const AuthorRegister = lazy(() => import('./pages/author/AuthorRegister'));
const AuthorReset = lazy(() => import('./pages/author/AuthorReset'));
const AuthorOnboarding = lazy(() => import('./pages/author/AuthorOnboarding'));
const AuthorDashboard = lazy(() => import('./pages/author/AuthorDashboard'));
const DashboardOverview = lazy(() => import('./pages/author/DashboardOverview'));
const AuthorSubmission = lazy(() => import('./pages/author/AuthorSubmission'));
const AuthorDrafts = lazy(() => import('./pages/author/AuthorDrafts'));
const SubmissionList = lazy(() => import('./pages/author/SubmissionList'));
const SubmissionDetail = lazy(() => import('./pages/author/SubmissionDetail'));
const SubmissionSuccess = lazy(() => import('./pages/author/SubmissionSuccess'));
const RevisionSubmission = lazy(() => import('./pages/author/RevisionSubmission'));
const GalleyProofReview = lazy(() => import('./pages/author/GalleyProofReview'));

// Import Reviewer Components
const ReviewerLogin = lazy(() => import('./pages/reviewer/ReviewerLogin'));
const ReviewerDashboard = lazy(() => import('./pages/reviewer/ReviewerDashboard'));
const ReviewerInvitationResponse = lazy(() => import('./pages/reviewer/ReviewerInvitationResponse'));
const ReviewerFirstLogin = lazy(() => import('./pages/reviewer/ReviewerFirstLogin'));
const ReviewerForgotPassword = lazy(() => import('./pages/reviewer/ReviewerForgotPassword'));
const ReviewDetail = lazy(() => import('./pages/reviewer/ReviewDetail'));
const MyReviews = lazy(() => import('./pages/reviewer/MyReviews'));
const ReviewerPasswordReset = lazy(() => import('./pages/reviewer/ReviewerPasswordReset'));
const ReviewSubmissionForm = lazy(() => import('./pages/reviewer/ReviewSubmissionForm'));
const ReviewerProfile = lazy(() => import('./pages/reviewer/ReviewerProfile'));
const ReviewerMessaging = lazy(() => import('./components/reviewer/ReviewerMessaging'));

// Import Admin Dashboard Components
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboardHome = lazy(() => import('./pages/admin/AdminDashboardHome'));
const Submissions = lazy(() => import('./pages/admin/Submissions'));
const SubmissionDetailLayout = lazy(() => import('./pages/admin/SubmissionDetailLayout'));
const SubmissionOverviewTab = lazy(() => import('./pages/admin/SubmissionOverviewTab'));
const SubmissionAuthorsTab = lazy(() => import('./pages/admin/SubmissionAuthorsTab'));
const SubmissionPlagiarismTab = lazy(() => import('./pages/admin/SubmissionPlagiarismTab'));
const SubmissionReviewersTab = lazy(() => import('./pages/admin/SubmissionReviewersTab'));
const SubmissionHistoryTab = lazy(() => import('./pages/admin/SubmissionHistoryTab'));
const Reviewers = lazy(() => import('./pages/admin/Reviewers'));
const ManuscriptReviewers = lazy(() => import('./pages/admin/ManuscriptReviewers'));
const SubmissionsBatchManager = lazy(() => import('./components/admin/SubmissionsBatchManager'));
const Editors = lazy(() => import('./pages/admin/Editors'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const EmailTemplates = lazy(() => import('./pages/admin/EmailTemplates'));
const EmailManagement = lazy(() => import('./pages/admin/EmailManagement'));
const AssignEditors = lazy(() => import('./pages/admin/AssignEditors'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const ContentEditor = lazy(() => import('./pages/admin/ContentEditor'));
const AnnouncementEditor = lazy(() => import('./pages/admin/AnnouncementEditor'));

// Reviewer Management Components
const ReviewerCommunicationHub = lazy(() => import('./components/admin/ReviewerCommunicationHub'));
const COIManagementDashboard = lazy(() => import('./components/admin/COIManagementDashboard'));
const AdminReviewManagement = lazy(() => import('./components/admin/AdminReviewManagement'));
const EditorialDecisions = lazy(() => import('./components/admin/EditorialDecisions'));
const ManuscriptTool = lazy(() => import('./pages/admin/ManuscriptTool'));
const EditorialBoardManager = lazy(() => import('./pages/admin/EditorialBoardManager'));
const AuthorProfile = lazy(() => import('./pages/author/AuthorProfile'));
const PublicationList = lazy(() => import('./pages/author/PublicationList'));
const PublicationDetail = lazy(() => import('./pages/author/PublicationDetail'));

// Plagiarism Review Components
const PlagiarismReview = lazy(() => import('./pages/admin/PlagiarismReview'));

const VolumeManager = lazy(() => import('../src/components/admin/VolumeManager'));
const IssueManager = lazy(() => import('../src/components/admin/IssueManager'));
const ArticleManager = lazy(() => import('../src/components/admin/ArticleManager'));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const canonicalUrl = `${SITE_URL}${pathname}`;

  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
};

// Route-level Suspense fallback. Sized to roughly match typical page content
// height so the swap-in of the real page doesn't itself register as a layout
// shift (see the CLS fix in the same audit as this lazy-loading change).
const RouteFallback = () => (
  <div className="min-h-[70vh] flex items-center justify-center" aria-busy="true" aria-live="polite">
    <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
  </div>
);

function App() {
  return (
    <div className="app-container">
      <Router>
        <HeaderProvider>
          <UnifiedAuthProvider>
          <EditorProvider>
          <ManagingEditorProvider>
          <AssociateEditorProvider>
          <EditorInChiefProvider>
          <EthicsQualityEditorProvider>
          <EditorialOfficeProvider>
          <EditorialAssistantProvider>
          <SectionEditorProvider>
            <ScrollToTop />
            <div className="min-h-screen bg-gray-50">
              <Navbar />
              <main className="pt-16">
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/articles" element={<Articles />} />
                  <Route path="/articles/:articleId" element={<ArticleDetail />} />
                  <Route path="/journal-archives" element={<JournalArchives />} />
                  <Route path="/for-authors" element={<ForAuthors />} />
                  <Route path="/for-reviewers" element={<ForReviewers />} />
                  <Route path="/editorial-board" element={<EditorialBoard />} />
                  <Route path="/disclaimer" element={<Advice />} />
                  <Route path="/guidelines" element={<Policies />} />
                  <Route path="/peer-review-policy" element={<PeerReviewPolicy />} />
                  <Route path="/policies" element={<Navigate to="/guidelines" replace />} />
                  <Route path="/open-access-statement" element={<OpenAccessStatement />} />
                  <Route path="/manuscript-submission" element={<ManuscriptSubmissionForm />} />

                  {/* Editor Routes */}
                  <Route path="/editor/login" element={<EditorLogin />} />
                  <Route path="/editor/dashboard" element={
                    <EditorProtectedRoute>
                      <EditorDashboard />
                    </EditorProtectedRoute>
                  } />

                  <Route path="/editor/submission/:id" element={
                    <EditorProtectedRoute>
                      <EditorSubmissionDetail />
                    </EditorProtectedRoute>
                  } />

                  {/* Managing Editor Routes */}
                  <Route path="/managing-editor/login" element={<ManagingEditorLogin />} />
                  <Route path="/managing-editor/dashboard" element={
                    <ManagingEditorProtectedRoute>
                      <ManagingEditorDashboard />
                    </ManagingEditorProtectedRoute>
                  } />

                  {/* Associate Editor Routes */}
                  <Route path="/associate-editor/login" element={<AssociateEditorLogin />} />
                  <Route path="/associate-editor/dashboard" element={
                    <AssociateEditorProtectedRoute>
                      <AssociateEditorDashboard />
                    </AssociateEditorProtectedRoute>
                  } />

                  {/* Editor-in-Chief Routes */}
                  <Route path="/editor-in-chief/login" element={<EditorInChiefLogin />} />
                  <Route path="/editor-in-chief/dashboard" element={
                    <EditorInChiefProtectedRoute>
                      <EditorInChiefDashboard />
                    </EditorInChiefProtectedRoute>
                  } />

                  {/* Ethics & Quality Editor Routes */}
                  <Route path="/ethics-quality-editor/login" element={<EthicsQualityEditorLogin />} />
                  <Route path="/ethics-quality-editor/dashboard" element={
                    <EthicsQualityEditorProtectedRoute>
                      <EthicsQualityEditorDashboard />
                    </EthicsQualityEditorProtectedRoute>
                  } />

                  {/* Editorial Office Routes */}
                  <Route path="/editorial-office/login" element={<EditorialOfficeLogin />} />
                  <Route path="/editorial-office/dashboard" element={
                    <EditorialOfficeProtectedRoute>
                      <EditorialOfficeDashboard />
                    </EditorialOfficeProtectedRoute>
                  } />

                  {/* Editorial Assistant Routes */}
                  <Route path="/editorial-assistant/login" element={<EditorialAssistantLogin />} />
                  <Route path="/editorial-assistant/dashboard" element={
                    <EditorialAssistantProtectedRoute>
                      <EditorialAssistantDashboard />
                    </EditorialAssistantProtectedRoute>
                  } />

                  {/* Section Editor Routes */}
                  <Route path="/section-editor/login" element={<SectionEditorLogin />} />
                  <Route path="/section-editor/dashboard" element={
                    <SectionEditorProtectedRoute>
                      <SectionEditorDashboard />
                    </SectionEditorProtectedRoute>
                  } />

                  {/* Unified login: one front door for Admin + all 9 editorial roles */}
                  <Route path="/login" element={<UnifiedLogin />} />
                  <Route path="/dashboard" element={<UnifiedDashboard />} />

                  {/* Author Dashboard Routes */}
                  <Route path="/author/login" element={<AuthorLogin />} />
                  <Route path="/author/register" element={<AuthorRegister />} />
                  <Route path="/author/reset-password" element={<AuthorReset />} />
                  <Route path="/author/onboarding" element={<AuthorOnboarding />} />

                  {/* Reviewer Routes */}
                  <Route path="/reviewer/login" element={<ReviewerLogin />} />
                  <Route path="/reviewer/invitation/:token" element={<ReviewerInvitationResponse />} />
                  <Route path="/reviewer/first-login/:token" element={<ReviewerFirstLogin />} />
                  <Route path="/reviewer/forgot-password" element={<ReviewerForgotPassword />} />
                  <Route path="/reviewer/reset-password" element={<ReviewerPasswordReset />} />
                  <Route path="/reviewer/reviews" element={<ReviewerDashboard />}>
                    <Route index element={<MyReviews />} />
                  </Route>
                  <Route path="/reviewer/reviews/:reviewId" element={<ReviewerDashboard />}>
                    <Route index element={<ReviewDetail />} />
                    <Route path="submit" element={<ReviewSubmissionForm />} />
                  </Route>
                  <Route path="/reviewer/profile" element={<ReviewerDashboard />}>
                    <Route index element={<ReviewerProfile />} />
                  </Route>

                  {/* Protected Reviewer Dashboard Routes */}
                  <Route path="/reviewer/dashboard" element={<ReviewerDashboard />}>
                    <Route index element={<div>Dashboard Overview</div>} />
                    <Route path="reviews" element={<MyReviews />} />
                    <Route path="reviews/:reviewId" element={<ReviewDetail />} />
                    <Route path="reviews/:reviewId/submit" element={<ReviewSubmissionForm />} />
                    <Route path="profile" element={<ReviewerProfile />} />
                    <Route path="messages" element={<ReviewerMessaging />} />
                  </Route>

                  {/* Protected Author Dashboard Routes */}
                  <Route path="/author/dashboard" element={<AuthorDashboard />}>
                    <Route index element={<DashboardOverview />} />
                    <Route path="overview" element={<DashboardOverview />} />
                    <Route path="submit" element={<AuthorSubmission />} />
                    <Route path="drafts" element={<AuthorDrafts />} />
                    <Route path="submissions" element={<SubmissionList />} />
                    <Route path="submissions/:id" element={<SubmissionDetail />} />
                    <Route path="submissions/:id/success" element={<SubmissionSuccess />} />
                    <Route path="submissions/:id/revise" element={<RevisionSubmission />} />
                    <Route path="submissions/:id/galley" element={<GalleyProofReview />} />
                    <Route path="profile" element={<AuthorProfile />} />
                    <Route path="publications" element={<PublicationList />} />
                    <Route path="publications/:id" element={<PublicationDetail />} />
                  </Route>

                  {/* New Global Admin Dashboard routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout><AdminDashboardHome /></AdminLayout>} />
                  <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboardHome /></AdminLayout>} />

                  {/* Admin Submission Management Routes */}
                  <Route path="/admin/submissions" element={<AdminLayout><Submissions /></AdminLayout>} />
                  <Route path="/admin/submissions-batch" element={<AdminLayout><SubmissionsBatchManager /></AdminLayout>} />
                  <Route path="/admin/submissions/:id" element={<AdminLayout><SubmissionDetailLayout /></AdminLayout>}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<SubmissionOverviewTab />} />
                    <Route path="authors" element={<SubmissionAuthorsTab />} />
                    <Route path="plagiarism" element={<SubmissionPlagiarismTab />} />
                    <Route path="reviewers" element={<SubmissionReviewersTab />} />
                    <Route path="history" element={<SubmissionHistoryTab />} />
                  </Route>
                  {/* New Admin Management Routes */}
                  {/* Volumes -> Issues -> Articles drill-down, all under AdminLayout now (formerly split off into /admin/old/*) */}
                  <Route path="/admin/issues" element={<AdminLayout><VolumeManager /></AdminLayout>} />
                  <Route path="/admin/issues/:volumeId" element={<AdminLayout><IssueManager /></AdminLayout>} />
                  <Route path="/admin/articles" element={<AdminLayout><VolumeManager /></AdminLayout>} />
                  <Route path="/admin/articles/:issueId" element={<AdminLayout><ArticleManager /></AdminLayout>} />
                  <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
                  <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
                  <Route path="/admin/content" element={<AdminLayout><ContentEditor /></AdminLayout>} />
                  <Route path="/admin/announcement" element={<AdminLayout><AnnouncementEditor /></AdminLayout>} />
                  <Route path="/admin/reviewers" element={<AdminLayout><Reviewers /></AdminLayout>} />
                  <Route path="/admin/manuscript-reviewers" element={<AdminLayout><ManuscriptReviewers /></AdminLayout>} />
                  <Route path="/admin/editors" element={<AdminLayout><Editors /></AdminLayout>} />
                  <Route path="/admin/analytics" element={<AdminLayout><Analytics /></AdminLayout>} />
                  <Route path="/admin/email-templates" element={<AdminLayout><EmailTemplates /></AdminLayout>} />
                  <Route path="/admin/email-management" element={<AdminLayout><EmailManagement /></AdminLayout>} />
                  <Route path="/admin/assign-editors" element={<AdminLayout><AssignEditors /></AdminLayout>} />

                  {/* Reviewer Management Routes */}
                  <Route path="/admin/review-management" element={<AdminLayout><AdminReviewManagement /></AdminLayout>} />
                  <Route path="/admin/editorial-decisions" element={<AdminLayout><EditorialDecisions /></AdminLayout>} />
                  <Route path="/admin/reviewer-communication" element={<AdminLayout><ReviewerCommunicationHub /></AdminLayout>} />
                  <Route path="/admin/coi-management" element={<AdminLayout><COIManagementDashboard /></AdminLayout>} />
                  <Route path="/admin/reviewer-analytics" element={<AdminLayout><Analytics /></AdminLayout>} />
                  <Route path="/admin/manuscript-tool" element={<AdminLayout><ManuscriptTool /></AdminLayout>} />
                  <Route path="/admin/editorial-board" element={<AdminLayout><EditorialBoardManager /></AdminLayout>} />

                  {/* Plagiarism Review Routes */}
                  <Route path="/admin/plagiarism-review" element={<AdminLayout><PlagiarismReview /></AdminLayout>} />
                </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          </SectionEditorProvider>
          </EditorialAssistantProvider>
          </EditorialOfficeProvider>
          </EthicsQualityEditorProvider>
          </EditorInChiefProvider>
          </AssociateEditorProvider>
          </ManagingEditorProvider>
          </EditorProvider>
          </UnifiedAuthProvider>
        </HeaderProvider>
      </Router>
    </div>
  );
}

export default App;
